import React, { useState, useEffect, useCallback, useContext } from "react";
import { useApolloClient } from "@apollo/client";
import { SelectPagination } from "@foris/avocado-suite";
import { BookingContext } from "../../context/BookingContext";
import { Types } from "../../context/request.reducer";
import { BaseQuery, PageInfo } from "@models/ISchema";
import { usersQuery } from "../../graphql/users.query";
import { useTranslation } from "react-i18next";
import useBookingContextUrl from "@modules/booking/hooks/useBookingContextUrl";

interface Props {
  className?: string;
}

const UserSelector: React.FC<Props> = ({ className = "" }) => {
  const { scenario } = useBookingContextUrl();
  const { state, dispatch } = useContext(BookingContext);
  const client = useApolloClient();
  const defaultUser =
    typeof state?.request?.userResponsible === "object"
      ? state?.request?.userResponsible?.value
        ? state?.request?.userResponsible
        : null
      : { label: state?.request?.userResponsible };
  const [userPage, setUserPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [optionsUser, setOptionsUser] = useState(null);
  const [valueUser, setValueUser] = useState(defaultUser);
  const [termToSearch, setTermToSearch] = useState("");
  const { t } = useTranslation();

  const getUsers = useCallback(
    async (searchTerm = "", page = 1) => {
      const optionsPerPage = 10;
      setLoading(true);

      try {
        const variables = {
          query: usersQuery,
          variables: {
            scenarioId: scenario,
            filter: {
              fields: searchTerm
                ? {
                    name: {
                      contains: searchTerm,
                    },
                  }
                : undefined,
              pagination: {
                page,
                size: optionsPerPage,
              },
            },
          },
        };

        const { data } = await client.query(variables);
        const dataQuery: BaseQuery = data?.base;
        const pageInfo: PageInfo = dataQuery?.users?.pageInfo;
        const users = dataQuery?.users;

        const options = [];
        if (users) {
          users.items.forEach(user => {
            const lastName = user.lastName == null ? "" : user.lastName;

            options.push({
              label: `${user.name} ${lastName}`,
              value: user.id,
            });
          });
        }

        setOptionsUser(options);

        return { pageInfo, options };
      } catch (error) {
        console.error(error);
        setLoading(false);
        return null;
      }
    },
    [client],
  );

  const loadUserOptions = async (searchTerm: string) => {
    let searchPage = 1;

    if (termToSearch === searchTerm) {
      searchPage = userPage;
    } else {
      setTermToSearch(searchTerm);
    }

    const { pageInfo, options: userOptions } = await getUsers(searchTerm, searchPage);
    setUserPage(userPage + 1);

    return {
      options: userOptions,
      hasMore: pageInfo?.hasNextPage,
      additional: {
        page: userPage,
      },
    };
  };

  useEffect(() => {
    if (!optionsUser && !loading) {
      getUsers();
    } else if (optionsUser?.length && !loading && state?.request?.userResponsible && !valueUser) {
      const userLabel: string = state?.request?.userResponsible?.value;
      const user = optionsUser.find((optionUser: any) => optionUser?.label === userLabel);
      setValueUser(user);
    }
  }, [optionsUser]);

  const selectedUser = state?.request?.userResponsible;
  const request = { ...state?.request };

  return (
    <SelectPagination
      label={t("booking.booking-request.form.responsible.label")}
      error={selectedUser?.error ? "Ingresa un nombre" : null}
      placeholder={t("booking.booking-request.form.responsible.placeholder")}
      loadOptions={loadUserOptions}
      className={className}
      value={valueUser}
      debounceTimeout={500}
      onChange={(selectedUser: any) => {
        if (selectedUser) {
          setValueUser(selectedUser);
          request.userResponsible = selectedUser;

          dispatch({
            type: Types.SetUserResponsible,
            payload: {
              value: selectedUser?.value,
              label: selectedUser?.label,
              id: selectedUser?.value,
              error: false,
            },
          });
        } else {
          setValueUser(null);
          const requests = { ...state?.request };
          requests.userResponsible = selectedUser.user;

          dispatch({
            type: Types.SetUserResponsible,
            payload: {
              value: selectedUser?.value,
              label: selectedUser?.label,
              id: selectedUser?.value,
              error: false,
            },
          });
        }
      }}
    />
  );
};

export default UserSelector;
